import { Injectable } from '@angular/core';
import { ApiService } from '@shared/services/api.service';
import { IResponsible, IResponsibleUser, IUpdateResponsible, IUser } from '@shared/interfaces';
import { IListView } from '@shared/interfaces/various/IListView';
import { ResponsibleService } from '@shared/services/responsible.service';
import { IQueryOrganisation } from '@shared/interfaces/queries/IQueryOrganisation';
import { convertQueryToParams } from '@shared/helpers/query-converter';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { IStartReportingPeriod, IUnPublishMass } from '@shared/interfaces/responsible/IStartReportingPeriod';

@Injectable({
    providedIn: 'root'
})
export class OrganisationService extends ResponsibleService {
    constructor(public apiService: ApiService) {
        super(apiService);
    }

    getOrganisation(query: IQueryOrganisation): Promise<IListView<IResponsible>> {
        return this.apiService.get(`/organisation${convertQueryToParams<IQueryOrganisation>(query)}`);
    }

    getOrganisationFlat(query: IQueryOrganisation): Promise<IPaginatedResult<IResponsible>> {
        return this.apiService.get(`/organisation/flat${convertQueryToParams<IQueryOrganisation>(query)}`);
    }

    deleteOrganisation(item: IResponsible): Promise<boolean> {
        return this.apiService.delete(`/organisation/${encodeURIComponent(item.id)}`);
    }

    override createItem(item: IUpdateResponsible): Promise<IResponsible> {
        return this.apiService.post('/organisation', this.updateResponsible(item));
    }

    override updateItem(item: IUpdateResponsible): Promise<IResponsible> {
        return this.apiService.patch('/organisation', this.updateResponsible(item));
    }

    startReportingPeriod(command: IStartReportingPeriod): Promise<Array<string>> {
        return this.apiService.post('/organisation/start-reporting', command);
    }

    unPublishMass(command: IUnPublishMass): Promise<Array<string>> {
        return this.apiService.post('/organisation/mass-un-publish', command);
    }

    unPublishReportForNode(id: string): Promise<boolean> {
        return this.apiService.post('/organisation/un-publish/' + encodeURIComponent(id));
    }

    resetReportForNode(id: string): Promise<boolean> {
        return this.apiService.post('/organisation/reset/' + encodeURIComponent(id));
    }

    updateResponsible(item: IUpdateResponsible) {
        item.mainResponsible = item.mainResponsible.map((e: IUser) =>
            !e.isExternal
                ? e
                : {
                      id: e.id,
                      fullName: e.userData.fullName,
                      eMail: e.userData.eMail,
                      fullAccess: true
                  }
        ) as IResponsibleUser[];
        item.mainSpecialists = item.mainSpecialists.map((e: IUser) =>
            !e.isExternal
                ? e
                : {
                      id: e.id,
                      fullName: e.userData.fullName,
                      eMail: e.userData.eMail,
                      fullAccess: true
                  }
        ) as IResponsibleUser[];
        item.otherResponsible = item.otherResponsible.map((e: IUser) =>
            !e.isExternal
                ? e
                : {
                      id: e.id,
                      fullName: e.userData.fullName,
                      eMail: e.userData.eMail,
                      fullAccess: true
                  }
        ) as IResponsibleUser[];
        item.otherSpecialists = item.otherSpecialists.map((e: IUser) =>
            !e.isExternal
                ? e
                : {
                      id: e.id,
                      fullName: e.userData.fullName,
                      eMail: e.userData.eMail,
                      fullAccess: true
                  }
        ) as IResponsibleUser[];

        return item;
    }

    public updateDeactivationState(id: string, deactivated: boolean): Promise<void> {
        return this.apiService.patch(`/organisation/${encodeURIComponent(id)}/deactivation`, { deactivated });
    }
}
